<script setup>
// Right Chat - card do Kanban proprio.
import { computed } from 'vue';

const props = defineProps({
  task: { type: Object, required: true },
  accountId: { type: [String, Number], required: true },
  dragging: { type: Boolean, default: false },
  dropBefore: { type: Boolean, default: false },
});

const emit = defineEmits(['dragstart', 'dragend', 'dragover']);

const PRIORITY_LABELS = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Media',
  low: 'Baixa',
};

const priorityLabel = computed(
  () => PRIORITY_LABELS[props.task.priority] || props.task.priority || '-'
);

const formattedValue = computed(() => {
  const raw = props.task.value;
  if (raw === null || raw === undefined || raw === '') return '';
  const numeric = Number(raw);
  if (Number.isNaN(numeric)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numeric);
});

const dueDate = computed(() => {
  if (!props.task.due_date) return null;
  const parsed = new Date(props.task.due_date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
});

const dueLabel = computed(() =>
  dueDate.value
    ? new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(dueDate.value)
    : ''
);

const isOverdue = computed(
  () =>
    !!dueDate.value &&
    dueDate.value.getTime() < Date.now() &&
    props.task.status === 'open'
);

const conversationUrl = computed(() =>
  props.task.conversation_display_id
    ? `/app/accounts/${props.accountId}/conversations/${props.task.conversation_display_id}`
    : ''
);

const labels = computed(() => props.task.labels || []);
</script>

<template>
  <article
    class="rk-card"
    :class="{ 'rk-card--dragging': dragging, 'rk-card--drop-before': dropBefore }"
    draggable="true"
    :data-task-id="task.id"
    @dragstart="emit('dragstart', task, $event)"
    @dragend="emit('dragend', task, $event)"
    @dragover="emit('dragover', task, $event)"
  >
    <header class="rk-card__head">
      <h4 class="rk-card__title" :title="task.title">{{ task.title }}</h4>
      <span class="rk-card__priority" :data-priority="task.priority">
        {{ priorityLabel }}
      </span>
    </header>

    <p v-if="task.description" class="rk-card__description">
      {{ task.description }}
    </p>

    <ul v-if="labels.length" class="rk-card__labels">
      <li v-for="label in labels" :key="label" class="rk-card__label">
        {{ label }}
      </li>
    </ul>

    <footer class="rk-card__foot">
      <span v-if="formattedValue" class="rk-card__value">
        {{ formattedValue }}
      </span>
      <span
        v-if="dueLabel"
        class="rk-card__due"
        :class="{ 'rk-card__due--late': isOverdue }"
      >
        {{ isOverdue ? 'Venceu' : 'Vence' }} {{ dueLabel }}
      </span>
      <a
        v-if="conversationUrl"
        class="rk-card__link"
        :href="conversationUrl"
        draggable="false"
      >
        Abrir conversa
      </a>
    </footer>
  </article>
</template>

<style scoped>
.rk-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgb(15 23 42 / 6%);
  cursor: grab;
}

.rk-card:active {
  cursor: grabbing;
}

.rk-card--dragging {
  opacity: 0.45;
}

.rk-card--drop-before::before {
  position: absolute;
  top: -5px;
  right: 0;
  left: 0;
  height: 3px;
  content: '';
  background: #4f46e5;
  border-radius: 3px;
}

.rk-card__head {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  justify-content: space-between;
}

.rk-card__title {
  margin: 0;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: #1e293b;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.rk-card__priority {
  flex-shrink: 0;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
}

.rk-card__priority[data-priority='urgent'] {
  background: #fee2e2;
  color: #b91c1c;
}

.rk-card__priority[data-priority='high'] {
  background: #ffedd5;
  color: #c2410c;
}

.rk-card__priority[data-priority='low'] {
  background: #ecfdf5;
  color: #047857;
}

.rk-card__description {
  margin: 0;
  overflow: hidden;
  font-size: 12px;
  color: #64748b;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.rk-card__labels {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.rk-card__label {
  padding: 1px 6px;
  font-size: 10px;
  color: #3730a3;
  background: #eef2ff;
  border-radius: 4px;
}

.rk-card__foot {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  font-size: 11px;
  color: #64748b;
}

.rk-card__value {
  font-weight: 600;
  color: #047857;
}

.rk-card__due--late {
  font-weight: 600;
  color: #b91c1c;
}

.rk-card__link {
  margin-left: auto;
  color: #4f46e5;
  text-decoration: none;
}

.rk-card__link:hover {
  text-decoration: underline;
}

.dark .rk-card {
  background: #1e293b;
  border-color: #334155;
}

.dark .rk-card__title {
  color: #e2e8f0;
}

.dark .rk-card__description,
.dark .rk-card__foot {
  color: #94a3b8;
}

.dark .rk-card__priority {
  background: #334155;
  color: #cbd5e1;
}

.dark .rk-card__label {
  background: #312e81;
  color: #c7d2fe;
}
</style>
